import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { tapLight } from '../../lib/haptics';

interface AtBat {
  id: string;
  pitcherId: string;
  inning: number;
  pitches: ('B' | 'S')[];
  outcome: 'K' | 'BB' | 'HBP' | 'IN_PLAY' | null;
  timestamp: string;
}

interface CatcherEvent {
  id: string;
  catcherId: string;
  type: 'PB' | 'BLOCK';
  inning: number;
  timestamp: string;
}

interface PitchingTrackerPanelProps {
  pitcher: any;
  catcher?: any;
  currentInning: number;
  pitchSequence: AtBat[];
  catcherEvents?: CatcherEvent[];
  eligibleCatchers?: any[];
  onSaveAtBat: (atBat: AtBat) => Promise<void>;
  onUndoAtBat: () => Promise<void>;
  onSaveCatcherEvent?: (type: 'PB' | 'BLOCK', catcherId: string) => Promise<void>;
  onUndoCatcherEvent?: () => Promise<void>;
  onAssignCatcher?: (playerId: string) => Promise<void>;
  /** Daily pitch cap for the current pitcher (from the active ruleset). Undefined
      = no cap to warn against. */
  dailyMax?: number;
  /** Pitches this pitcher has already thrown today in OTHER games (same day), so
      the live warning reflects the true running daily total. */
  priorTodayPitches?: number;
  onClose: () => void;
  inline?: boolean;
  /**
   * The at-bat in progress, owned by the live engine so it survives this panel
   * unmounting (a tab switch, a fold or unfold that changes the layout). When
   * absent the panel keeps it locally, as it always did.
   */
  pitches?: ('B' | 'S')[];
  onPitchesChange?: (pitches: ('B' | 'S')[]) => void;
}

const OUTCOME_LABELS: Record<string, React.ReactNode> = {
  K: 'K',
  BB: 'BB',
  HBP: 'HBP',
  IN_PLAY: '■',
};

export function PitchingTrackerPanel({
  pitcher,
  catcher,
  currentInning,
  pitchSequence,
  catcherEvents = [],
  eligibleCatchers = [],
  onSaveAtBat,
  onUndoAtBat,
  onSaveCatcherEvent,
  onUndoCatcherEvent,
  onAssignCatcher,
  dailyMax,
  priorTodayPitches = 0,
  onClose,
  inline = false,
  pitches,
  onPitchesChange,
}: PitchingTrackerPanelProps) {
  const [localPitches, setLocalPitches] = useState<('B' | 'S')[]>([]);
  const controlled = pitches !== undefined && !!onPitchesChange;
  const currentPitches = controlled ? pitches! : localPitches;
  const setCurrentPitches = (next: ('B' | 'S')[] | ((prev: ('B' | 'S')[]) => ('B' | 'S')[])) => {
    const value = typeof next === 'function' ? next(currentPitches) : next;
    if (controlled) onPitchesChange!(value);
    else setLocalPitches(value);
  };
  const [isSaving, setIsSaving] = useState(false);
  // When a catcher event is tapped with no catcher set, hold the pending event
  // type while the coach picks who's behind the plate.
  const [pendingCatcherEvent, setPendingCatcherEvent] = useState<'PB' | 'BLOCK' | null>(null);

  // Catcher development counts for the player currently behind the plate.
  const catcherPB = catcher ? catcherEvents.filter(e => e.catcherId === catcher.id && e.type === 'PB').length : 0;
  const catcherBlocks = catcher ? catcherEvents.filter(e => e.catcherId === catcher.id && e.type === 'BLOCK').length : 0;

  const handleCatcherEvent = async (type: 'PB' | 'BLOCK') => {
    if (!onSaveCatcherEvent) return;
    if (!catcher) {
      // No one is set at catcher — ask who's behind the plate first.
      setPendingCatcherEvent(type);
      return;
    }
    await onSaveCatcherEvent(type, catcher.id);
  };

  const handlePickCatcher = async (playerId: string) => {
    await onAssignCatcher?.(playerId);
    if (pendingCatcherEvent && onSaveCatcherEvent) {
      await onSaveCatcherEvent(pendingCatcherEvent, playerId);
    }
    setPendingCatcherEvent(null);
  };

  const pitcherAtBats = pitchSequence.filter(
    ab => ab.pitcherId === pitcher?.id && ab.outcome !== null
  );

  const allPitches = pitcherAtBats.flatMap(ab => ab.pitches);
  const totalPitches = allPitches.length;
  const totalStrikes = allPitches.filter(p => p === 'S').length;
  const strikePercent = totalPitches > 0
    ? Math.round((totalStrikes / totalPitches) * 100)
    : null;

  const FPSCount = pitcherAtBats.filter(ab => ab.pitches[0] === 'S').length;
  const FPSPercent = pitcherAtBats.length > 0
    ? Math.round((FPSCount / pitcherAtBats.length) * 100)
    : null;

  const Ks = pitcherAtBats.filter(ab => ab.outcome === 'K').length;
  const BBs = pitcherAtBats.filter(ab => ab.outcome === 'BB').length;
  const HBPs = pitcherAtBats.filter(ab => ab.outcome === 'HBP').length;

  // Running pitch totals
  const inningPitches = pitcherAtBats
    .filter(ab => ab.inning === currentInning)
    .flatMap(ab => ab.pitches).length + currentPitches.length;
  const gamePitches = totalPitches + currentPitches.length;

  const currentBalls = currentPitches.filter(p => p === 'B').length;
  const currentStrikes = currentPitches.filter(p => p === 'S').length;

  // ── Live daily-limit warning ───────────────────────────────────────────────
  // gamePitches already folds in the in-progress at-bat (currentPitches), so this
  // updates on every ball/strike tap — the heads-up fires BEFORE the at-bat ends,
  // which is what hard-cap leagues need. Daily total spans all of today's games.
  const dailyTotal = priorTodayPitches + gamePitches;
  const dailyPct = dailyMax && dailyMax > 0 ? (dailyTotal / dailyMax) * 100 : 0;
  const dailyLeft = dailyMax ? dailyMax - dailyTotal : null;
  const limitWarning: { level: 'over' | 'near'; text: string } | null =
    !dailyMax || dailyMax <= 0 ? null
    : dailyTotal >= dailyMax
      ? { level: 'over', text: `Daily limit reached: ${dailyTotal}/${dailyMax} pitches today` }
    : dailyPct >= 90
      ? { level: 'near', text: `${dailyLeft} pitch${dailyLeft === 1 ? '' : 'es'} left today (${dailyTotal}/${dailyMax})` }
    : null;

  const saveAtBat = async (pitches: ('B' | 'S')[], outcome: 'K' | 'BB' | 'HBP' | 'IN_PLAY') => {
    if (!pitcher || isSaving) return;
    setIsSaving(true);
    const atBat: AtBat = {
      id: String(Date.now()),
      pitcherId: pitcher.id,
      inning: currentInning,
      pitches,
      outcome,
      timestamp: new Date().toISOString(),
    };
    try {
      await onSaveAtBat(atBat);
      setCurrentPitches([]);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePitch = async (type: 'B' | 'S') => {
    tapLight(); // every ball/strike tap
    const newPitches = [...currentPitches, type];
    const balls = newPitches.filter(p => p === 'B').length;
    if (balls >= 4) {
      // Auto-walk — save immediately without updating currentPitches
      await saveAtBat(newPitches, 'BB');
    } else {
      setCurrentPitches(newPitches);
    }
  };

  const handleOutcome = async (outcome: 'K' | 'BB' | 'HBP' | 'IN_PLAY') => {
    tapLight(); // K / BB / HBP / In Play
    await saveAtBat(currentPitches, outcome);
  };

  const handleUndo = async () => {
    if (currentPitches.length > 0) {
      setCurrentPitches(prev => prev.slice(0, -1));
    } else {
      // Undo last completed at-bat (e.g. an auto-walk), restore its pitches
      const last = pitcherAtBats[pitcherAtBats.length - 1];
      if (!last) return;
      await onUndoAtBat();
      setCurrentPitches(last.pitches);
    }
  };

  const canUndo = currentPitches.length > 0 || pitcherAtBats.length > 0;

  const completedAtBats = [...pitchSequence]
    .filter(ab => ab.pitcherId === pitcher?.id && ab.outcome !== null)
    .reverse();

  const content = (
    <>
      {/* Header */}
      <div className={cn(
        "flex items-start justify-between px-5 pb-4 border-b border-hairline shrink-0",
        inline ? "pt-4" : "pt-5"
      )}>
        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-ink-2">Pitching Tracker</p>
          <p className="text-base font-black italic uppercase leading-tight">
            {pitcher?.name ?? 'No Pitcher'}
          </p>
        </div>
        <div className="flex gap-4 items-start">
          <div className="flex flex-col items-center">
            <span className="text-[9px] font-bold text-ink-2 uppercase tracking-widest">Inn {currentInning}</span>
            <span className="text-base font-black italic uppercase leading-tight">{inningPitches}P</span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-[9px] font-bold text-ink-2 uppercase tracking-widest">Game</span>
            <span className="text-base font-black italic uppercase leading-tight">{gamePitches}P</span>
          </div>
          {!inline && (
            <button onClick={onClose} className="p-2 -mr-2 hover:bg-hairline rounded-full transition-colors">
              <X className="w-5 h-5 text-ink-2" />
            </button>
          )}
        </div>
      </div>

      {/* Stats Strip */}
      <div className="grid grid-cols-5 gap-1.5 px-4 py-3 border-b border-hairline shrink-0">
        {([
          { key: 'strike_pct', label: 'Strike%', value: strikePercent !== null ? `${strikePercent}%` : '—' },
          { key: 'fps',        label: 'FPS%',    value: FPSPercent !== null ? `${FPSPercent}%` : '—' },
          { key: 'k',          label: 'K',       value: String(Ks) },
          { key: 'bb',         label: 'BB',      value: String(BBs) },
          { key: 'hbp',        label: 'HBP',     value: String(HBPs) },
        ] as { key: string; label: React.ReactNode; value: string }[]).map(stat => (
          <div key={stat.key} className="bg-surface rounded-xl p-2 flex flex-col items-center">
            <span className="text-[8px] font-bold text-ink-2 uppercase tracking-widest leading-none mb-1">{stat.label}</span>
            <span className="text-sm font-black text-ink">{stat.value}</span>
          </div>
        ))}
      </div>

      {/* At-bat history */}
      <div className={cn(
        "px-4 py-3 space-y-2",
        inline ? "" : "flex-1 overflow-y-auto"
      )}>
        {completedAtBats.length === 0 && (
          <p className="text-center text-ink-2 text-xs font-bold uppercase tracking-widest py-4">
            No at-bats yet
          </p>
        )}
        {completedAtBats.map((ab) => (
          <div key={ab.id} className="flex items-center justify-between bg-surface rounded-2xl px-4 py-3 border border-hairline">
            <div className="flex items-center gap-1 flex-wrap">
              {ab.pitches.map((p, j) => (
                <span key={j} className={cn(
                  'w-5 h-5 rounded-full text-[8px] font-black flex items-center justify-center',
                  p === 'S' ? 'bg-emerald-500 text-ink' : 'bg-track text-ink-2'
                )}>
                  {p}
                </span>
              ))}
              {ab.pitches.length === 0 && (
                <span className="text-[9px] text-ink-2 font-bold">0 pitches</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <span className="text-[9px] font-bold text-ink-2 uppercase">Inn {ab.inning}</span>
              <span className={cn(
                'text-xs font-black px-2 py-1 rounded-lg',
                ab.outcome === 'K' ? 'bg-red-500/20 text-danger' :
                ab.outcome === 'BB' ? 'bg-blue-500/20 text-blue-400' :
                ab.outcome === 'HBP' ? 'bg-amber-500/20 text-gold-ink' :
                'bg-hairline text-ink-2'
              )}>
                {OUTCOME_LABELS[ab.outcome!]}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Current At-bat */}
      <div className={cn(
        "border-t border-hairline bg-raised px-4 pt-4 space-y-4",
        inline ? "sticky bottom-0 pb-4 z-10" : "shrink-0 pb-6"
      )}>
        {/* Daily-limit heads-up — Claude-usage-style banner. Fires at 90% and at/over
            the cap, live per pitch, for hard-cap leagues. */}
        {limitWarning && (
          <div className={cn(
            "flex items-center gap-2.5 rounded-2xl px-4 py-3 border",
            limitWarning.level === 'over'
              ? "bg-red-500/15 border-red-500/40"
              : "bg-amber-500/15 border-amber-500/40"
          )}>
            <span className={cn(
              "text-base leading-none",
              limitWarning.level === 'over' ? "text-danger" : "text-gold-ink"
            )}>⚠</span>
            <span className={cn(
              "text-[11px] font-black uppercase tracking-widest leading-tight",
              limitWarning.level === 'over' ? "text-danger" : "text-gold-ink"
            )}>
              {limitWarning.text}
            </span>
          </div>
        )}

        {/* Pitch dots + count */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1.5 flex-wrap justify-center min-h-[24px]">
            {currentPitches.length === 0 ? (
              <span className="text-[9px] font-bold text-ink-2 uppercase tracking-widest">New At-Bat</span>
            ) : (
              currentPitches.map((p, i) => (
                <span key={i} className={cn(
                  'w-6 h-6 rounded-full text-[9px] font-black flex items-center justify-center',
                  p === 'S' ? 'bg-emerald-500 text-ink' : 'bg-track text-ink-2'
                )}>
                  {p}
                </span>
              ))
            )}
          </div>
          {currentPitches.length > 0 && (
            <p className="text-[10px] font-bold text-ink-2 uppercase tracking-widest">
              {currentBalls} Balls · {currentStrikes} Strikes
            </p>
          )}
        </div>

        {/* BALL / STRIKE buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => handlePitch('B')} disabled={isSaving}
            className="py-5 bg-hairline hover:bg-track border border-track rounded-2xl text-ink font-black text-xl uppercase italic tracking-widest transition-colors active:scale-95 disabled:opacity-50">
            Ball
          </button>
          <button onClick={() => handlePitch('S')} disabled={isSaving}
            className="py-5 bg-emerald-600 hover:bg-emerald-500 rounded-2xl text-ink font-black text-xl uppercase italic tracking-widest transition-colors active:scale-95 shadow-[0_0_16px_rgba(16,185,129,0.3)] disabled:opacity-50">
            Strike
          </button>
        </div>

        {/* Undo */}
        {canUndo && (
          <button onClick={handleUndo} disabled={isSaving}
            className="w-full text-[10px] font-black uppercase tracking-widest text-ink-2 hover:text-ink-2 transition-colors py-1 disabled:opacity-50">
            {currentPitches.length === 0 ? '← Undo Walk' : '← Undo Last Pitch'}
          </button>
        )}

        {/* Outcome row */}
        <div className="grid grid-cols-4 gap-2">
          {(['K', 'BB', 'HBP', 'IN_PLAY'] as const).map(outcome => {
            const disabled = isSaving || (outcome !== 'HBP' && currentPitches.length === 0);
            return (
              <button key={outcome} onClick={() => handleOutcome(outcome)} disabled={disabled}
                className={cn(
                  'py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95',
                  disabled
                    ? 'bg-surface text-ink-mute border border-hairline cursor-not-allowed'
                    : outcome === 'K'
                    ? 'bg-red-500/20 text-danger border border-red-500/30 hover:bg-red-500/30'
                    : outcome === 'BB'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30'
                    : outcome === 'HBP'
                    ? 'bg-amber-500/20 text-gold-ink border border-amber-500/30 hover:bg-amber-500/30'
                    : 'bg-hairline text-ink-2 border border-track hover:bg-track'
                )}>
                {OUTCOME_LABELS[outcome]}
              </button>
            );
          })}
        </div>

        {/* Catcher development row */}
        {onSaveCatcherEvent && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-black uppercase tracking-widest text-ink-2">
                Catcher{catcher ? <span className="text-ink-2"> · {catcher.name}</span> : <span className="text-ink-2"> · none set</span>}
              </span>
              {catcher && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-ink-2">
                  {catcherBlocks} Blk · {catcherPB} PB
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleCatcherEvent('BLOCK')}
                disabled={isSaving}
                className="py-3 rounded-xl font-black text-xs uppercase tracking-widest bg-emerald-500/15 text-accent-ink border border-emerald-500/30 hover:bg-emerald-500/25 transition-all active:scale-95 disabled:opacity-50">
                Block
              </button>
              <button
                onClick={() => handleCatcherEvent('PB')}
                disabled={isSaving}
                className="py-3 rounded-xl font-black text-xs uppercase tracking-widest bg-red-500/15 text-danger border border-red-500/30 hover:bg-red-500/25 transition-all active:scale-95 disabled:opacity-50">
                Passed Ball
              </button>
            </div>
            {catcherEvents.length > 0 && onUndoCatcherEvent && (
              <button onClick={onUndoCatcherEvent} disabled={isSaving}
                className="w-full text-[10px] font-black uppercase tracking-widest text-ink-2 hover:text-ink-2 transition-colors py-1 disabled:opacity-50">
                ← Undo Last Catcher Event
              </button>
            )}
          </div>
        )}
      </div>

      {/* "Who's catching?" picker — shown when a catcher event is tapped with no catcher set */}
      <AnimatePresence>
        {pendingCatcherEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/60 flex items-end justify-center"
            onClick={() => setPendingCatcherEvent(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="w-full max-w-lg bg-surface rounded-t-3xl p-6 space-y-3"
              onClick={e => e.stopPropagation()}
            >
              <div className="mb-1">
                <h3 className="font-black uppercase text-sm text-ink">Who's catching?</h3>
                <p className="text-[10px] font-bold text-ink-2 uppercase tracking-widest mt-0.5">
                  Credit this {pendingCatcherEvent === 'PB' ? 'passed ball' : 'block'} and set them behind the plate
                </p>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {eligibleCatchers.map((p: any) => (
                  <button
                    key={p.id}
                    onClick={() => handlePickCatcher(p.id)}
                    className="w-full flex items-center space-x-3 px-4 py-3 bg-raised border border-hairline rounded-2xl hover:border-emerald-500/50 transition-colors text-left"
                  >
                    <div className="w-8 h-8 bg-hairline rounded-full flex items-center justify-center font-black text-[10px] shrink-0">
                      #{p.number || '?'}
                    </div>
                    <span className="font-black text-sm uppercase text-ink">{p.name}</span>
                  </button>
                ))}
                {eligibleCatchers.length === 0 && (
                  <p className="text-center text-ink-2 text-xs font-bold uppercase tracking-widest py-4">
                    No players on the field
                  </p>
                )}
              </div>
              <button onClick={() => setPendingCatcherEvent(null)} className="w-full py-3 text-[10px] font-black uppercase tracking-widest text-ink-2 hover:text-ink-2 transition-colors">
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (inline) {
    return <div className="flex flex-col bg-raised">{content}</div>;
  }

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed inset-0 z-[60] bg-raised flex flex-col"
    >
      {content}
    </motion.div>
  );
}
